// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file ComplexSelector.tsx
 * @input Uses React, StyleX, Field, Icon slots, Layer positioning, and usePopover
 * @output Exports a rich-selector shell with exact token-sized input and ghost triggers, plus an imperative open/close handle
 * @position Core implementation; consumed by index.ts
 *
 * SYNC: When modified, update:
 * - /packages/core/src/ComplexSelector/ComplexSelector.doc.mjs
 * - /packages/core/src/ComplexSelector/ComplexSelector.test.tsx
 * - /packages/core/src/ComplexSelector/index.ts
 * - /apps/storybook/stories/ComplexSelector.stories.tsx
 * - /packages/cli/assets/templates/blocks/components/ComplexSelector/ (showcase blocks)
 */
import React, { useCallback, useId, useImperativeHandle, useOptimistic, useRef, useTransition } from 'react';
import * as stylex from '@stylexjs/stylex';
import { Field, inputWrapperStyles } from "../Field/index.js";
import { Icon, renderIconSlot } from "../Icon/index.js";
import { Spinner } from "../Spinner/index.js";
import { useTranslator } from "../i18n/index.js";
import { layerAnimations } from "../Layer/layerAnimations.stylex.js";
import { usePopover } from "../Popover/usePopover.js";
import "../theme/tokens.stylex.js";
import { colorVars, durationVars, easeVars, fontWeightVars, radiusVars, sizeVars, spacingVars, typographyVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { mergeProps } from "../utils/index.js";
import { composeEventHandlers } from "../utils/composeEventHandlers.js";
import { focusOutlineStyles } from "../utils/focusOutline.stylex.js";
import { themeProps } from "../utils/themeProps.js";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const styles = {
  triggerContainer: {
    kVAEAm: "x1n2onr6",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "x1qughib",
    kOIVth: "x1txdalj",
    kzqmXN: "xh8yej3",
    k8WAf4: "xce4md1",
    kg3NbH: "xrrkdod",
    kMv6JI: "x9ynric",
    kGuDYH: "xcr08ib xdk3u5w",
    kLWn49: "x1kq96og",
    kMwMTN: "x1tgivj0",
    kkrTdU: "x1ypdohk",
    $$css: true
  },
  trigger: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "x1qughib",
    kOIVth: "x1txdalj",
    kzQI83: "x1iyjqo2",
    kmuXW: "xs83m0k",
    kCS8Yb: "x1r8uery",
    k7Eaqz: "xeuugli",
    kmVPX3: "x1717udv",
    kogj98: "x1ghz6dp",
    kMzoRj: "xc342km",
    ksu8eU: "xng3xce",
    kWkggS: "xjbqb8w",
    kMv6JI: "xjb2p0i",
    kGuDYH: "x1qlqyl8",
    kLWn49: "x15bjb6t",
    kMwMTN: "x1heor9g",
    kkrTdU: "x1ypdohk",
    kI3sdo: "x1a2a7pz",
    kaIpWk: "xh6dtrn",
    $$css: true
  },
  triggerText: {
    kzQI83: "x1iyjqo2",
    k7Eaqz: "xeuugli",
    kVQacm: "xb3r6kr",
    kg5iWk: "xlyipyv",
    khDVqt: "xuxw1ft",
    k9WMMc: "x1yc453h",
    $$css: true
  },
  placeholder: {
    kMwMTN: "xv1l7n4",
    $$css: true
  },
  triggerGhost: {
    kzqmXN: "x14atkfc",
    kMzoRj: "xc342km",
    kWkggS: "xjbqb8w",
    kKwaWg: "x1ilzqfv xq8i9tn",
    kGVxlE: "x1gnnqk1 x1irk71m x15ysqaf",
    k63SB2: "x1e4wzip",
    k1ekBW: "xrafxwg",
    k3aq6I: "x3oybdh xk4oym4",
    $$css: true
  },
  triggerGhostDisabled: {
    kKwaWg: "x18o3ruo",
    k3aq6I: "x1c071of x1pdlv7q",
    $$css: true
  },
  triggerIcon: {
    kmuXW: "x2lah0s",
    $$css: true
  },
  triggerIconRotation: {
    k1ekBW: "x11xpdln",
    kIyJzY: "xuedmi6",
    kAMwcw: "xlr8y92",
    k3nNDw: "x1g0ag68",
    $$css: true
  },
  triggerIconOpen: {
    k3aq6I: "x19jd1h0",
    $$css: true
  },
  popover: {
    k7Eaqz: "xrzjruh",
    $$css: true
  },
  content: {
    kB7OPa: "x9f619",
    kskxy: "xpnbb6b",
    kVQacm: "xysyzu8",
    kmVPX3: "x1b2ylru",
    $$css: true
  },
  sm: {
    kZKoxP: "x6k0iem",
    $$css: true
  },
  md: {
    kZKoxP: "x1ueg155",
    $$css: true
  },
  lg: {
    kZKoxP: "xssyfek",
    $$css: true
  },
  disabled: {
    kkrTdU: "x1h6gzvc",
    $$css: true
  }
};

/**
 * Imperative control surface for ComplexSelector, accessed via the `handleRef`
 * prop. Methods drive the same popover machinery as the built-in trigger, so
 * they respect focus restoration, light dismiss, and Escape. Prefer these
 * callbacks over mirroring open state in the parent — the selector owns its
 * visibility, and imperative calls avoid the focus-management pitfalls of
 * syncing an external `isOpen` prop.
 */

/**
 * A selector shell for rich, custom selection surfaces.
 *
 * ComplexSelector owns the field, trigger, popover, focus restore, and async
 * change action flow. Consumers provide the dialog content as a render function,
 * using the supplied `value`, `onChange`, and `close` helpers to compose the
 * right accessible structure for the custom selector.
 *
 * @example
 * ```
 * <ComplexSelector
 *   label="Fruit"
 *   value={value}
 *   onChange={setValue}
 *   triggerLabel={`${value.fruit} ${value.ripeness}`}>
 *   {(value, onChange, close) => (
 *     <FruitGrid
 *       value={value}
 *       onChange={nextValue => {
 *         onChange(nextValue);
 *         close();
 *       }}
 *     />
 *   )}
 * </ComplexSelector>
 * ```
 */
export function ComplexSelector({
  label,
  value,
  onChange,
  changeAction,
  children,
  triggerLabel,
  placeholder: placeholderFromProps,
  isLabelHidden = false,
  description,
  isOptional = false,
  isRequired = false,
  isDisabled = false,
  isLoading = false,
  status,
  statusVariant = 'attached',
  labelTooltip,
  size = 'md',
  variant = 'input',
  startIcon,
  width,
  placement = 'below',
  alignment = 'start',
  handleRef,
  contentXstyle,
  xstyle,
  className,
  style,
  'data-testid': testId,
  onClick: onClickProp,
  ...props
}) {
  const t = useTranslator();
  const placeholder = placeholderFromProps ?? t('@astryx.selector.placeholder');
  const effectiveStatusVariant = variant === 'ghost' && statusVariant === 'attached' ? 'detached' : statusVariant;
  const triggerId = useId();
  const labelId = useId();
  const contentId = useId();
  const descriptionId = useId();
  const statusMessageId = useId();
  const ariaDescribedBy = [description ? descriptionId : null, status?.message ? statusMessageId : null].filter(id => id != null).join(' ') || undefined;
  const triggerRef = useRef(null);
  const lastHideTimeRef = useRef(0);
  const [isPending, startTransition] = useTransition();
  const [optimisticValue, setOptimisticValue] = useOptimistic(value);
  const isBusy = isLoading || isPending;
  const handlePopoverHide = useCallback(() => {
    lastHideTimeRef.current = Date.now();
    triggerRef.current?.focus();
  }, []);
  const popover = usePopover({
    dialogLabel: label,
    hasCloseButton: false,
    hasAutoFocus: true,
    surfaceTarget: 'complex-selector-popup',
    onHide: handlePopoverHide
  });
  const isOpen = popover.isOpen;
  const handleTriggerClick = useCallback(() => {
    if (isDisabled || Date.now() - lastHideTimeRef.current < 50) {
      return;
    }
    if (popover.isOpen) {
      popover.hide();
    } else {
      popover.show();
    }
  }, [isDisabled, popover]);
  const close = useCallback(() => {
    popover.hide();
  }, [popover]);
  useImperativeHandle(handleRef, () => ({
    open: () => {
      if (!isDisabled) {
        popover.show();
      }
    },
    close: () => popover.hide(),
    toggle: () => {
      if (isDisabled) {
        return;
      }
      if (popover.isOpen) {
        popover.hide();
      } else {
        popover.show();
      }
    },
    isOpen: () => popover.isOpen
  }), [isDisabled, popover]);
  const commitValue = useCallback(nextValue => {
    onChange?.(nextValue);
    if (changeAction) {
      startTransition(async () => {
        setOptimisticValue(nextValue);
        await changeAction(nextValue);
      });
    }
  }, [changeAction, onChange, setOptimisticValue, startTransition]);
  const triggerContent = triggerLabel ?? placeholder;
  const content = /*#__PURE__*/_jsx("div", {
    id: contentId,
    ...stylex.props(styles.content, contentXstyle),
    children: children(optimisticValue, commitValue, close, {
      isOpen,
      isBusy,
      triggerId,
      contentId
    })
  });
  const selectorContent = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("div", {
      ref: popover.triggerRef,
      "data-testid": testId,
      ...props,
      onClick: composeEventHandlers(onClickProp, handleTriggerClick),
      ...mergeProps(themeProps('complex-selector', {
        variant,
        size,
        status: status?.type ?? null
      }), stylex.props(inputWrapperStyles.base, styles.triggerContainer, styles[size], variant === 'input' && focusOutlineStyles.focusWithin, variant === 'ghost' && styles.triggerGhost, variant === 'ghost' && focusOutlineStyles.focusWithin, isDisabled && inputWrapperStyles.disabled, variant === 'ghost' && isDisabled && styles.triggerGhostDisabled, isDisabled && styles.disabled, triggerLabel == null && styles.placeholder, xstyle), className, style),
      children: [startIcon && renderIconSlot(startIcon, {
        size: 'sm',
        color: 'secondary'
      }), /*#__PURE__*/_jsx("button", {
        ref: triggerRef,
        id: triggerId,
        type: "button",
        "aria-haspopup": "dialog",
        "aria-expanded": isOpen,
        "aria-controls": contentId,
        "aria-describedby": ariaDescribedBy,
        "aria-labelledby": labelId,
        "aria-required": isRequired ? 'true' : undefined,
        "aria-invalid": status?.type === 'error' ? 'true' : undefined,
        "aria-busy": isBusy || undefined,
        disabled: isDisabled,
        onKeyDown: event => {
          if (event.key === 'ArrowDown' && !isOpen && !isDisabled) {
            event.preventDefault();
            popover.show();
          }
        },
        ...{
          className: "x78zum5 x6s0dn4 x1qughib x1txdalj x1iyjqo2 xs83m0k x1r8uery xeuugli x1717udv x1ghz6dp xc342km xng3xce xjbqb8w xjb2p0i x1qlqyl8 x15bjb6t x1heor9g x1ypdohk x1a2a7pz xh6dtrn"
        },
        children: /*#__PURE__*/_jsx("span", {
          ...{
            className: "x1iyjqo2 xeuugli xb3r6kr xlyipyv xuxw1ft x1yc453h"
          },
          children: triggerContent
        })
      }), isBusy && /*#__PURE__*/_jsx(Spinner, {
        size: "sm"
      }), /*#__PURE__*/_jsx(Icon, {
        icon: "chevronDown",
        size: "sm",
        color: "secondary"
        // No wrapper: Icon's own span already provides the 16px box (`sm`)
        // and the secondary icon color the wrapper used to set, so the glyph
        // IS the trigger's icon element — one node carrying the box, the
        // color, the rotation, and the theme target.
        ,
        xstyle: [styles.triggerIcon, styles.triggerIconRotation, isOpen && styles.triggerIconOpen],
        ...themeProps('complex-selector-indicator-icon', {
          state: isOpen ? 'expanded' : 'collapsed'
        })
      })]
    }), popover.render(content, {
      placement,
      alignment,
      offset: spacingVars['--spacing-1'],
      xstyle: [styles.popover, layerAnimations[placement]]
    })]
  });
  return /*#__PURE__*/_jsx(Field, {
    label: label,
    isLabelHidden: isLabelHidden,
    description: description,
    inputID: triggerId,
    descriptionID: description ? descriptionId : undefined,
    labelID: labelId,
    isOptional: isOptional,
    isRequired: isRequired,
    isDisabled: isDisabled,
    status: status ? {
      type: status.type,
      message: status.message,
      messageID: status.message ? statusMessageId : undefined
    } : undefined,
    statusVariant: effectiveStatusVariant,
    labelTooltip: labelTooltip,
    width: width,
    children: selectorContent
  });
}
ComplexSelector.displayName = 'ComplexSelector';